import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { CT_BG, bandColor } from "@/lib/api";
import { Progress } from "@/components/ui/progress";

export const Shell = ({ title, subtitle, children, nav, wide = false }) => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const label = auth?.role === "student" ? auth.student?.name : auth?.user?.name;
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white/75 backdrop-blur-xl border-b border-slate-200/70">
        <div className={`${wide ? "max-w-[1720px]" : "max-w-7xl"} mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4`}>
          <div className="flex items-center gap-3 min-w-0">
            <img src="/gdiot-logo-wide.png" alt="CT Skill Assessment" className="h-11 w-auto object-contain shrink-0" data-testid="app-logo" />
            <div className="min-w-0">
              <h4 className="text-lg font-semibold truncate" data-testid="shell-title">{title}</h4>
              {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {nav}
            <span className="hidden sm:block text-sm text-slate-500">{label}</span>
            <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <main className={`${wide ? "max-w-[1720px]" : "max-w-7xl"} mx-auto px-4 sm:px-8 py-8`}>{children}</main>
    </div>
  );
};

export const CTGrid = ({ ct, testid = "ct-grid" }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={testid}>
    {(ct || []).map((c) => (
      <div key={c.ct_skill} className="soft-card p-5" data-testid={`ct-card-${c.ct_skill.replace(/\s+/g, "-").toLowerCase()}`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="h-3 w-3 rounded-full" style={{ background: CT_BG[c.ct_skill] }} />
          <span className="text-sm font-semibold text-slate-800">{c.ct_skill}</span>
        </div>
        <div className="text-3xl font-head font-bold text-slate-900">
          {c.percentage === null || c.percentage === undefined ? "—" : `${c.percentage}%`}
        </div>
        <p className="text-xs text-slate-500 mt-1">{c.correct}/{c.total} correct</p>
        <div className="mt-3">
          <Progress value={c.percentage || 0} className="h-2" />
        </div>
        <span className={`inline-block mt-3 text-xs px-2 py-1 rounded-full border ${bandColor(c.band)}`}>{c.band}</span>
      </div>
    ))}
  </div>
);

export const StatCard = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl sm:text-3xl font-head font-bold text-slate-900 mt-1">{value}</p>
  </div>
);
